if (!customElements.get('scenario-tab-showcase')) {
  customElements.define(
    'scenario-tab-showcase',
    class ScenarioTabShowcase extends HTMLElement {
      constructor() {
        super();
        this.current = 0;
        this.swipeThreshold = 50;
        this.dragStartX = 0;
        this.dragStartY = 0;
        this.dragDeltaX = 0;
        this.isPointerDown = false;
        this.activePointerId = null;
      }

      connectedCallback() {
        this.tabs = this.querySelectorAll('[data-sts-tab]');
        this.panels = this.querySelectorAll('[data-sts-panel]');
        this.descs = this.querySelectorAll('[data-sts-desc]');
        this.prevBtn = this.querySelector('[data-sts-prev]');
        this.nextBtn = this.querySelector('[data-sts-next]');
        this.nav = this.querySelector('[data-sts-nav]');
        this.panelsWrap = this.querySelector('[data-sts-panels]');
        this.track = this.querySelector('[data-sts-track]');
        this.total = this.tabs.length;
        this.loop = this.dataset.arrowLoop === 'true';

        if (!this.total) return;

        this.onResize = this.onLayoutChange.bind(this);
        this.onTabClick = this.handleTabClick.bind(this);
        this.onPrev = this.handlePrev.bind(this);
        this.onNext = this.handleNext.bind(this);
        this.onPointerDown = this.handlePointerDown.bind(this);
        this.onPointerMove = this.handlePointerMove.bind(this);
        this.onPointerEnd = this.handlePointerEnd.bind(this);
        this.onLostCapture = this.handleLostCapture.bind(this);
        this.onKeydown = this.handleKeydown.bind(this);

        window.addEventListener('resize', this.onResize);
        this.tabs.forEach((tab) => {
          tab.addEventListener('click', this.onTabClick);
          tab.addEventListener('keydown', this.onKeydown);
        });

        if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
        if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);

        if (this.panelsWrap && this.total > 1) {
          this.panelsWrap.addEventListener('pointerdown', this.onPointerDown);
          this.panelsWrap.addEventListener('pointermove', this.onPointerMove, { passive: false });
          this.panelsWrap.addEventListener('pointerup', this.onPointerEnd);
          this.panelsWrap.addEventListener('pointercancel', this.onPointerEnd);
          this.panelsWrap.addEventListener('lostpointercapture', this.onLostCapture);
        }

        if (Shopify.designMode) {
          this.onBlockSelect = this.handleBlockSelect.bind(this);
          document.addEventListener('shopify:block:select', this.onBlockSelect);
        }

        this.onLayoutChange();
        this.setActive(0, false);
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.onResize);
        this.tabs.forEach((tab) => {
          tab.removeEventListener('click', this.onTabClick);
          tab.removeEventListener('keydown', this.onKeydown);
        });
        if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
        if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);
        if (this.panelsWrap) {
          this.panelsWrap.removeEventListener('pointerdown', this.onPointerDown);
          this.panelsWrap.removeEventListener('pointermove', this.onPointerMove);
          this.panelsWrap.removeEventListener('pointerup', this.onPointerEnd);
          this.panelsWrap.removeEventListener('pointercancel', this.onPointerEnd);
          this.panelsWrap.removeEventListener('lostpointercapture', this.onLostCapture);
        }
        if (this.onBlockSelect) {
          document.removeEventListener('shopify:block:select', this.onBlockSelect);
        }
      }

      getSlideWidth() {
        return this.panelsWrap ? this.panelsWrap.clientWidth : 0;
      }

      getBoundedDragOffset(deltaX) {
        if (this.loop) return deltaX;
        if (this.current === 0 && deltaX > 0) return deltaX * 0.2;
        if (this.current === this.total - 1 && deltaX < 0) return deltaX * 0.2;
        return deltaX;
      }

      moveTrack(dragOffset, animate) {
        if (!this.track || !this.panelsWrap) return;
        const width = this.getSlideWidth();
        const x = -this.current * width + (dragOffset || 0);

        if (animate === false) {
          this.track.style.transition = 'none';
        } else {
          this.track.style.transition = '';
        }

        this.track.style.transform = `translate3d(${x}px, 0, 0)`;

        if (animate === false) {
          this.track.offsetHeight;
          this.track.style.transition = '';
        }
      }

      updateNavScrollable() {
        if (!this.nav) return;
        const previousJustify = this.nav.style.justifyContent;
        this.nav.style.justifyContent = 'flex-start';
        const isScrollable = this.nav.scrollWidth > this.nav.clientWidth + 2;
        this.nav.style.justifyContent = previousJustify;
        this.nav.classList.toggle('is-scrollable', isScrollable);
        if (!isScrollable) this.nav.scrollLeft = 0;
      }

      scrollActiveTabIntoView(behavior) {
        const activeTab = this.tabs[this.current];
        if (!activeTab || !this.nav || !this.nav.classList.contains('is-scrollable')) return;

        const navRect = this.nav.getBoundingClientRect();
        const tabRect = activeTab.getBoundingClientRect();
        const delta = tabRect.left - navRect.left - (navRect.width - tabRect.width) / 2;
        this.nav.scrollTo({
          left: this.nav.scrollLeft + delta,
          behavior: behavior || 'smooth',
        });
      }

      isVideoVisible(video) {
        return video.offsetParent !== null && video.getClientRects().length > 0;
      }

      syncPanelVideos() {
        this.panels.forEach((panel, i) => {
          panel.querySelectorAll('video').forEach((video) => {
            if (i === this.current && this.isVideoVisible(video) && video.dataset.stsAutoplay === 'true') {
              if (video.paused) video.play().catch(() => {});
            } else if (!video.paused) {
              video.pause();
            }
          });
        });
      }

      onLayoutChange() {
        this.updateNavScrollable();
        this.scrollActiveTabIntoView('auto');
        this.moveTrack(0, false);
        this.syncPanelVideos();
      }

      resetPanelDrag() {
        if (this.panelsWrap) this.panelsWrap.classList.remove('is-dragging');
      }

      setActive(index, animate) {
        if (index < 0) index = this.loop ? this.total - 1 : 0;
        if (index >= this.total) index = this.loop ? 0 : this.total - 1;
        if (animate === undefined) animate = true;
        this.current = index;

        this.tabs.forEach((tab, i) => {
          const active = i === this.current;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.tabIndex = active ? 0 : -1;
        });

        this.panels.forEach((panel, i) => {
          const active = i === this.current;
          panel.classList.toggle('is-active', active);
          panel.setAttribute('aria-hidden', active ? 'false' : 'true');
        });

        this.descs.forEach((desc, i) => {
          desc.classList.toggle('is-active', i === this.current);
        });

        this.resetPanelDrag();
        this.moveTrack(0, animate);
        this.syncPanelVideos();

        if (!this.loop && this.prevBtn) this.prevBtn.disabled = this.current === 0;
        if (!this.loop && this.nextBtn) this.nextBtn.disabled = this.current === this.total - 1;

        this.scrollActiveTabIntoView(animate === false ? 'auto' : 'smooth');
      }

      handleTabClick(event) {
        this.setActive(parseInt(event.currentTarget.getAttribute('data-sts-tab'), 10));
      }

      handlePrev() {
        this.setActive(this.current - 1);
      }

      handleNext() {
        this.setActive(this.current + 1);
      }

      handleKeydown(event) {
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          this.setActive(this.current + 1);
          this.tabs[this.current].focus();
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.setActive(this.current - 1);
          this.tabs[this.current].focus();
        } else if (event.key === 'Home') {
          event.preventDefault();
          this.setActive(0);
          this.tabs[this.current].focus();
        } else if (event.key === 'End') {
          event.preventDefault();
          this.setActive(this.total - 1);
          this.tabs[this.current].focus();
        }
      }

      handlePointerDown(event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this.isPointerDown = true;
        this.activePointerId = event.pointerId;
        this.dragStartX = event.clientX;
        this.dragStartY = event.clientY;
        this.dragDeltaX = 0;
        this.panelsWrap.classList.add('is-dragging');
        if (this.panelsWrap.setPointerCapture) {
          this.panelsWrap.setPointerCapture(event.pointerId);
        }
      }

      handlePointerMove(event) {
        if (!this.isPointerDown || event.pointerId !== this.activePointerId) return;
        this.dragDeltaX = event.clientX - this.dragStartX;
        const dragDeltaY = event.clientY - this.dragStartY;
        if (Math.abs(this.dragDeltaX) > Math.abs(dragDeltaY) && Math.abs(this.dragDeltaX) > 10) {
          event.preventDefault();
        }
        this.moveTrack(this.getBoundedDragOffset(this.dragDeltaX), false);
      }

      handlePointerEnd(event) {
        if (!this.isPointerDown || (event.pointerId !== this.activePointerId && event.type !== 'pointercancel')) return;
        this.isPointerDown = false;
        this.activePointerId = null;

        if (this.panelsWrap.releasePointerCapture && event.pointerId != null) {
          try {
            this.panelsWrap.releasePointerCapture(event.pointerId);
          } catch (err) {}
        }

        const deltaX = this.dragDeltaX;
        const deltaY = event.clientY - this.dragStartY;
        this.resetPanelDrag();

        if (Math.abs(deltaX) >= this.swipeThreshold && Math.abs(deltaX) > Math.abs(deltaY)) {
          this.setActive(deltaX < 0 ? this.current + 1 : this.current - 1);
        } else {
          this.moveTrack(0, true);
        }

        this.dragDeltaX = 0;
      }

      handleLostCapture() {
        if (!this.isPointerDown) return;
        this.isPointerDown = false;
        this.activePointerId = null;
        this.resetPanelDrag();
        this.moveTrack(0, true);
      }

      handleBlockSelect(event) {
        if (event.detail.sectionId !== this.dataset.sectionId) return;
        const panel = this.querySelector(`[data-block-id="${event.detail.blockId}"]`);
        if (!panel) return;
        const index = parseInt(panel.getAttribute('data-sts-panel'), 10);
        if (!Number.isNaN(index)) this.setActive(index);
      }
    }
  );
}
